import Highcharts from 'highcharts';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import merge from 'lodash/merge';
import sum from 'lodash/sum';
import {Component} from 'panel';

import {abbreviateNumber3SigFigs, commaizeNumber, htmlEncodeString} from 'common/util';
import {truncateMiddle} from 'common/util/string';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {getChartSegmentColorCombo, primaryTextColor} from 'common/util/colors';
import {TooltipItemType} from 'common/components/chart-tooltip/types';
import mpFonts from 'common/stylesheets/mixins/fonts.json';

import {ChartDataItem, TooltipFormatter, TooltipFormatterParams} from './types';
import template from './index.jade';

export class PieChart extends Component<EmptyObj> {
  public static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'chart-data': {type: `json`, deprecatedMsg: `use props to pass chart data instead`}, // TODO clean up
      'chart-options-override': {type: `string`, default: ``, description: `Overriden option in for highcharts`},
      'disable-animation': {type: `boolean`, description: `Should animation be disabled`},
      size: {
        type: `number`,
        default: 0,
        description: `Size of the pie chart. Using wrapping element size if not specified`,
      },
    };
  }

  public get config() {
    return {
      template,
    };
  }

  public connectedCallback() {
    super.connectedCallback();

    if (!this.chartData) {
      throw new Error(`"chartData" is a required component prop`);
    }

    this.highcharts = new Highcharts.Chart(this.el.children[0], this.getChartOptions());
    this.updateChart();

    this.observer = new MutationObserver((mutations) => this.handleAttributeChange(mutations));
    this.observer.observe(this.el, {attributes: true, childList: false, subtree: false});

    // For backbone template support. TODO: clean up
    if (this.attr(`chart-data`)) {
      this.chartData = this.attr(`chart-data`);
    }
  }

  public disconnectedCallback() {
    if (this.highcharts) {
      this.highcharts.destroy();
      delete this.highcharts;
    }
    this.observer.disconnect();
    super.disconnectedCallback();
  }

  private highcharts: any;
  private observer: MutationObserver;
  private _total: number;
  private _chartData: any[];
  private _tooltipFormatter: TooltipFormatter;

  private handleAttributeChange(mutations: MutationRecord[]) {
    if (mutations.find((m) => m.attributeName === `size`)) {
      this.highcharts.setSize(this.size, this.size);
    }

    if (mutations.find((m) => m.attributeName === `chart-options-override`)) {
      // Use of merge has potential security issues, since we are using lodash >= 4.17.5 it is safe
      // eslint-disable-next-line prototype-pollution-security-rules/detect-merge-objects
      this.highcharts.update(merge(this.getDefaultChartOptions(), this.getJSONAttribute(`chart-options-override`)));
    }

    // deprecated; do not use
    // TODO clean up
    if (mutations.find((m) => m.attributeName === `chart-data`)) {
      this.chartData = this.attr(`chart-data`);
    }
  }

  private dataLabelsFormatter() {
    return function (this: TODO) {
      const label = this.key;
      return `
        <div class="pie-segment-label">
          ${truncateMiddle(label, 30)}
        </div>
      `;
    };
  }

  set tooltipFormatter(val: TooltipFormatter) {
    this._tooltipFormatter = val;
  }

  get tooltipFormatter() {
    return this._tooltipFormatter;
  }

  private defaultTooltipFormatter({name, value, percent, color}: TooltipFormatterParams) {
    const formatPercent = (decimal) => {
      return Math.round(decimal * Math.pow(10, 2)) / Math.pow(10, 2) + `%`;
    };

    const sections = [
      {
        rows: [
          {
            items: [
              {
                text: truncateMiddle(name, 45),
                type: TooltipItemType.Strong,
              },
            ],
          },
        ],
      },
      {
        rows: [
          {
            items: [
              {
                text: isNumber(value) ? commaizeNumber(value) : ``,
                type: TooltipItemType.Data,
              },
            ],
          },
          {
            items: [
              {
                text: isNumber(percent) ? formatPercent(percent) : ``,
                type: TooltipItemType.Data,
              },
              {
                text: `of total`,
                type: TooltipItemType.Text,
              },
            ],
          },
        ],
      },
    ];

    return `<mp-chart-tooltip title-color="${color}" sections='${htmlEncodeString(JSON.stringify(sections))}'>`;
  }

  private createTooltipFormatter(): TooltipFormatter {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const component = this;
    return function (this: TODO) {
      const params: TooltipFormatterParams = {
        name: this.key,
        value: this.point.y,
        percent: this.point.percentage,
        color: this.point.color,
        point: this.point,
      };
      return component.tooltipFormatter
        ? component.tooltipFormatter(params)
        : component.defaultTooltipFormatter(params);
    };
  }

  private getDefaultChartOptions() {
    return {
      chart: {
        height: this.size,
        type: `pie`,
        width: this.size,
        style: {
          fontFamily: mpFonts.fontFamilyPrimary,
          fontSize: `12px`,
        },
      },
      credits: {
        enabled: false,
      },
      title: {
        align: `center`,
        style: {
          color: primaryTextColor,
          fontSize: false,
          fontWeight: `bold`,
        },
        text: abbreviateNumber3SigFigs(this.total),
        verticalAlign: `middle`,
        y: 10,
      },
      plotOptions: {
        pie: {
          center: [`50%`, `50%`],
          dataLabels: {
            enabled: false,
            formatter: this.dataLabelsFormatter(),
          },
          shadow: false,
          size: `100%`,
          slicedOffset: 0,
        },
        series: {
          animation: this.attr(`disable-animation`) ? false : 1000,
        },
      },
      tooltip: {
        backgroundColor: `#fff`,
        padding: 0,
        borderRadius: 10,
        borderWidth: 0,
        formatter: this.createTooltipFormatter(),
        hideDelay: 0,
        shadow: false,
        useHTML: true,
      },
      series: [
        {
          data: this.chartData,
          innerSize: `60%`,
        },
      ],
    };
  }

  private get size() {
    let size = this.attr(`size`);
    if (!size) {
      const computedStyle = window.getComputedStyle(this.parentElement);
      size = Math.min(parseFloat(computedStyle.height), parseFloat(computedStyle.width));
    }
    return size;
  }

  private get total() {
    return this._total || 0;
  }

  private set chartData(data: ChartDataItem[]) {
    if (!isArray(data) || isEmpty(data)) {
      return;
    }
    this._total = sum(data.map((item) => item.value));
    data = data.map((item, idx) => {
      return {
        ...item,
        value: +item.value,
        color: item.color || getChartSegmentColorCombo(idx, `pieChart`).primary, // assign default primary colors if there's none
        percent: item.percent,
        secondaryColor: item.secondaryColor || getChartSegmentColorCombo(idx, `pieChart`).secondary, // assign default secondary colors if there's none
        name: item.name,
        y: item.pieChartValue || item.value,
      };
    });

    if (!isEqual(this._chartData, data)) {
      this._chartData = data;
      this.updateChart();
    }
  }

  private get chartData() {
    return this._chartData || [];
  }

  private getChartOptions() {
    const defaultChartOptions = this.getDefaultChartOptions();
    // Use of merge has potential security issues, since we are using lodash >= 4.17.5 it is safe
    // eslint-disable-next-line prototype-pollution-security-rules/detect-merge-objects
    return merge(defaultChartOptions, this.getJSONAttribute(`chart-options-override`));
  }

  private updateChart() {
    if (this.highcharts) {
      this.highcharts.update(
        this.getChartOptions(),
        true, // redraw after update
        true, // oneToOne: add/remove series when they change
      );
    }
  }
}

export const PieChartEl = defineAndMakeJsxFactory(`mp-pie-chart`, PieChart);
